import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import api from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { Calendar, Clock, DollarSign, Info, ArrowLeft } from 'lucide-react';

export default function BookingPage() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [hours, setHours] = useState(1);
  const [notes, setNotes] = useState('');

  const { data } = useQuery(['listing', uuid], () =>
    api.get(`/listings/${uuid}`).then(r => r.data)
  );

  const bookingMutation = useMutation(
    (payload) => api.post('/bookings', payload),
    {
      onSuccess: (res) => {
        navigate(`/dashboard/bookings/${res.data.booking.uuid}`);
      }
    }
  );

  const listing = data?.listing;
  if (!listing) return <div className="skeleton h-96 rounded-2xl" />;

  const minHours = listing.min_booking_hours || 1;
  const maxHours = listing.max_booking_hours || 24;

  const getEndTime = () => {
    if (!startTime) return '';
    const [h, m] = startTime.split(':').map(Number);
    const end = new Date(2000, 0, 1, h + hours, m);
    return `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
  };

  const subtotal = listing.price_per_hour * hours;
  const commission = subtotal * 0.1;
  const total = subtotal + commission;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!date || !startTime) return;
    const startDateTime = `${date}T${startTime}:00`;
    const [h, m] = startTime.split(':').map(Number);
    const endDate = new Date(`${date}T${startTime}:00`);
    endDate.setHours(endDate.getHours() + hours);
    const endDateTime = endDate.toISOString().slice(0, 19);

    bookingMutation.mutate({
      listing_id: listing.id,
      start_time: startDateTime,
      end_time: endDateTime,
      special_requests: notes,
    });
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="max-w-2xl mx-auto" dir="rtl">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg bg-slate-700/50 text-slate-400 hover:text-white">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-2xl font-bold text-white">إتمام الحجز</h1>
      </div>

      {/* Listing Summary */}
      <div className="glass rounded-2xl p-5 mb-6 flex gap-4">
        <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
          {listing.primary_image ? (
            <img src={`${process.env.REACT_APP_API_URL}/${listing.primary_image}`} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-slate-700" />
          )}
        </div>
        <div>
          <h3 className="font-bold text-white">{listing.title}</h3>
          <p className="text-slate-400 text-sm">{listing.city}، {listing.country}</p>
          <p className="text-primary-400 font-medium mt-1">{listing.price_per_hour} {listing.currency}/ساعة</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="glass rounded-2xl p-6 space-y-5">
          <h3 className="font-bold text-white border-b border-slate-700 pb-3">تفاصيل الحجز</h3>

          <div>
            <label className="block text-sm text-slate-400 mb-2 flex items-center gap-1.5">
              <Calendar size={14} /> تاريخ الحجز *
            </label>
            <input
              type="date"
              min={today}
              value={date}
              onChange={e => setDate(e.target.value)}
              required
              className="form-input w-full"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2 flex items-center gap-1.5">
                <Clock size={14} /> وقت البدء *
              </label>
              <input
                type="time"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                required
                className="form-input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">عدد الساعات *</label>
              <select
                value={hours}
                onChange={e => setHours(Number(e.target.value))}
                className="form-input w-full"
              >
                {Array.from({ length: maxHours - minHours + 1 }, (_, i) => i + minHours).map(h => (
                  <option key={h} value={h}>{h} {h === 1 ? 'ساعة' : 'ساعات'}</option>
                ))}
              </select>
            </div>
          </div>

          {startTime && (
            <div className="flex items-center gap-2 p-3 bg-primary-500/10 rounded-xl text-sm text-primary-400">
              <Info size={14} />
              <span>وقت الانتهاء: {getEndTime()}</span>
            </div>
          )}

          <div>
            <label className="block text-sm text-slate-400 mb-2">ملاحظات خاصة (اختياري)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              className="form-input w-full resize-none"
              placeholder="أي طلبات أو ملاحظات إضافية..."
            />
          </div>
        </div>

        {/* Price Summary */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-bold text-white border-b border-slate-700 pb-3 mb-4">ملخص التكلفة</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-slate-400">
              <span>{listing.price_per_hour} {listing.currency} × {hours} ساعة</span>
              <span>{subtotal.toFixed(2)} {listing.currency}</span>
            </div>
            <div className="flex justify-between text-slate-400">
              <span>عمولة المنصة (10%)</span>
              <span>{commission.toFixed(2)} {listing.currency}</span>
            </div>
            <div className="border-t border-slate-700 pt-2 flex justify-between text-white font-bold text-lg">
              <span>الإجمالي</span>
              <span className="text-primary-400">{total.toFixed(2)} {listing.currency}</span>
            </div>
          </div>
        </div>

        {bookingMutation.isError && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
            {bookingMutation.error?.response?.data?.message || 'حدث خطأ. يرجى المحاولة مرة أخرى.'}
          </div>
        )}

        <button
          type="submit"
          disabled={bookingMutation.isLoading || !date || !startTime}
          className="w-full btn-primary py-4 text-base flex items-center justify-center gap-2"
        >
          <DollarSign size={20} />
          {bookingMutation.isLoading ? 'جارٍ الإنشاء...' : `تأكيد الحجز — ${total.toFixed(2)} ${listing.currency}`}
        </button>
      </form>
    </div>
  );
}
